import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { SlidersHorizontal } from "lucide-react";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";

const CONDITIONS = ["all", "Seperti Baru", "Sangat Baik", "Baik", "Cukup"];
const SORTS = [
  { v: "newest", l: "Terbaru" },
  { v: "popular", l: "Terpopuler" },
  { v: "price_low", l: "Harga Terendah" },
  { v: "price_high", l: "Harga Tertinggi" },
];

export default function Home() {
  const [params] = useSearchParams();
  const q = params.get("q") || "";
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cat, setCat] = useState("all");
  const [cond, setCond] = useState("all");
  const [sort, setSort] = useState("newest");

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data));
  }, []);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    const { data } = await api.get("/products", {
      params: { q, category: cat, condition: cond, sort },
    });
    setProducts(data);
    setLoading(false);
  }, [q, cat, cond, sort]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  return (
    <div>
      {/* Hero */}
      <div className="brand-gradient rounded-3xl p-6 sm:p-10 text-white relative overflow-hidden mb-6">
        <div className="relative z-10 max-w-lg">
          <div className="inline-block bg-white/15 text-xs px-3 py-1 rounded-full mb-3">Kalabiru • Preloved Marketplace</div>
          <h1 className="text-2xl sm:text-4xl font-extrabold leading-tight">Barang preloved kece, harga bersahabat.</h1>
          <p className="mt-2 text-white/80 text-sm sm:text-base">Belanja aman dengan Kalabiru Safe Pay atau jual barangmu sekarang.</p>
        </div>
        <div className="absolute -right-8 -bottom-10 w-48 h-48 rounded-full bg-white/10" />
        <div className="absolute right-24 -top-10 w-32 h-32 rounded-full bg-white/10" />
      </div>

      {/* Categories */}
      <div className="flex overflow-x-auto no-scrollbar gap-2 pb-2 mb-4">
        <button
          data-testid="category-all"
          onClick={() => setCat("all")}
          className={`shrink-0 px-4 py-2 rounded-full text-sm font-medium border transition ${cat === "all" ? "bg-[#0B4DA2] text-white border-[#0B4DA2]" : "bg-white text-gray-600 border-gray-200"}`}
        >
          Semua
        </button>
        {categories.map((c) => (
          <button
            key={c.slug}
            data-testid={`category-${c.slug}`}
            onClick={() => setCat(c.slug)}
            className={`shrink-0 px-4 py-2 rounded-full text-sm font-medium border transition ${cat === c.slug ? "bg-[#0B4DA2] text-white border-[#0B4DA2]" : "bg-white text-gray-600 border-gray-200"}`}
          >
            {c.name}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 mb-5 flex-wrap">
        <div className="flex items-center gap-1 text-gray-500 text-sm">
          <SlidersHorizontal className="w-4 h-4" /> Filter:
        </div>
        <select
          data-testid="filter-condition"
          value={cond}
          onChange={(e) => setCond(e.target.value)}
          className="px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white outline-none"
        >
          {CONDITIONS.map((c) => (
            <option key={c} value={c}>{c === "all" ? "Semua Kondisi" : c}</option>
          ))}
        </select>
        <select
          data-testid="filter-sort"
          value={sort}
          onChange={(e) => setSort(e.target.value)}
          className="px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white outline-none"
        >
          {SORTS.map((s) => (
            <option key={s.v} value={s.v}>{s.l}</option>
          ))}
        </select>
        {q && <span className="text-sm text-gray-500">Hasil untuk "<b className="text-[#0B4DA2]">{q}</b>"</span>}
      </div>

      {loading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-4">
          {Array.from({ length: 10 }).map((_, i) => (
            <div key={i} className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
              <div className="aspect-square bg-gray-100 animate-pulse" />
              <div className="p-3 space-y-2">
                <div className="h-4 bg-gray-100 rounded animate-pulse w-1/2" />
                <div className="h-3 bg-gray-100 rounded animate-pulse" />
              </div>
            </div>
          ))}
        </div>
      ) : products.length === 0 ? (
        <div className="text-center py-20 text-gray-400">Tidak ada produk ditemukan.</div>
      ) : (
        <div data-testid="product-feed" className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-4">
          {products.map((p, i) => (
            <ProductCard key={p.id} product={p} index={i} />
          ))}
        </div>
      )}
    </div>
  );
}
